import type { BufferAttribute, Object3D } from 'three'
import { EventDispatcher, Vector2, Vector3 } from 'three'

import { domElementsAt, pointerEventToVec2 } from '@/lib/three-d-editor/utils'
import { getClosestVertex, moveOnPlane } from '@/lib/three-d-resources/geometry/utils'
import type { Tool } from '@/lib/three-d-viewer/tools/ToolManager'
import type { World, HitResult } from '~/lib/three-d-resources/world'
import type { TextLabel } from '~/lib/three-d-resources/labels-manager'

export type SelectionToolEventMap = {
  'label-select': {
    textLabel?: TextLabel
    drillLabel?: Object3D
    isRepositioning?: boolean
  }
  'label-move': {
    textLabel?: TextLabel
    drillLabel?: Object3D
  }
}

class SelectionTool extends EventDispatcher<SelectionToolEventMap> implements Tool {
  id: 'select' = 'select'
  private _grabbedTextLabel: TextLabel | undefined = undefined
  private _grabbedDrillLabel: Object3D | undefined = undefined
  private _editingTextLabel: TextLabel | undefined = undefined
  private _selectedDrillLabel: Object3D | undefined = undefined
  private _lastMousePosition = new Vector2()
  private _isDragging = false
  private _isDraggingFlagpole = false
  private _mode: 'editing' | 'selecting' = 'selecting'

  constructor(private world: World) {
    super()
  }

  activate(): void {}

  deactivate(): void {
    this.world.targetVisualizer?.hide()
    this.selectDrillLabel(undefined)
  }

  onMouseMove(e: MouseEvent, mousePos: Vector2): void {
    if (!this.world.labelsManager) return

    const camera = this.world.currentCamera
    const { _grabbedTextLabel: textLabel, _grabbedDrillLabel: drillLabel } = this

    const labelHit = this.world.hitTest(mousePos, { includeLabels: true })
    const domHit = domElementsAt(e).find(el => el.dataset.threedEntity)
    const setCursor = this.setCursor.bind(this, domHit ?? (e.currentTarget as HTMLElement))

    if (!(textLabel || drillLabel)) {
      if (labelHit?.grabbable) {
        setCursor('grab')
      } else if (labelHit?.label || labelHit?.drillLabel) {
        if (this._mode === 'editing') {
          setCursor('text')
        } else {
          setCursor('move')
        }
      } else if (!this._isDragging) {
        setCursor('none')
      }
      return
    }

    this._isDragging = true

    if (this._isDraggingFlagpole) {
      setCursor('grabbing')
      const nodeHit = this.world.hitTest(mousePos)
      if (nodeHit) this.moveFlagpole(nodeHit)
      return
    }

    if (textLabel) {
      const labelPosition = new Vector3(textLabel.offsetX, -textLabel.offsetY, textLabel.offsetZ).add(textLabel.point)
      const newPos = moveOnPlane(labelPosition, this._lastMousePosition, mousePos, camera).sub(textLabel.point)

      const label = this.world.labelsManager.labelsObjectMap.get(textLabel.id)
      if (!label) return

      const vertexData = getClosestVertex(label.cutout, textLabel.point)

      this._grabbedTextLabel = this.world.labelsManager.updateLabel(textLabel.id, {
        offsetX: newPos.x,
        offsetY: -newPos.y,
        offsetZ: newPos.z,
        flagpoleIndex: vertexData.index,
      })

      setCursor('move')

      this.dispatchEvent({ type: 'label-move', textLabel })
    }

    if (drillLabel && this._mode !== 'editing') {
      // TODO: we should move this logic into drillLabelManger
      const container = this.world.drillDataSetManager?.drillLabelManager.getDrillLabelObject(drillLabel.userData.drillLabelId)!
      const { flagpole, apiData, mesh, labelContainerGroup, labelOrientationHelper, auto_scale } = container
      if (auto_scale) return
      const labelOrigin = new Vector3().copy(apiData as unknown as Vector3)

      const containerWorldSpace = new Vector3().addVectors(labelContainerGroup.position, labelOrigin)
      const newPos = moveOnPlane(containerWorldSpace, this._lastMousePosition, mousePos, camera).sub(labelOrigin)

      labelContainerGroup.position.copy(newPos)

      if (mesh) {
        const labelAnchorPoint = new Vector3().copy(apiData as unknown as Vector3)
        const labelVertexAttachmentData = getClosestVertex(mesh, labelAnchorPoint)

        const labelVertexArray = mesh.geometry.getAttribute('position').array as Float32Array

        const vertexLocalAttachmentPoint = new Vector3().fromArray(labelVertexArray, labelVertexAttachmentData.index * 3)
        const vertexWorldAttachmentPoint = vertexLocalAttachmentPoint.clone().applyQuaternion(labelContainerGroup.quaternion)

        labelOrientationHelper.position.copy(labelContainerGroup.position).add(vertexWorldAttachmentPoint)

        const flagpolePos = flagpole.geometry.getAttribute('position') as BufferAttribute
        const newEndPosition = labelOrientationHelper.position
        flagpolePos.setXYZ(1, newEndPosition.x, newEndPosition.y, newEndPosition.z)
        flagpolePos.needsUpdate = true
        flagpole.geometry.computeBoundingSphere()

        container.offset.copy(new Vector3().addVectors(labelOrientationHelper.position, vertexLocalAttachmentPoint.multiplyScalar(-1)))
        container.flagpoleIndex = labelVertexAttachmentData.index
      }

      this.dispatchEvent({ type: 'label-move', drillLabel })
    }

    this._lastMousePosition.copy(mousePos)
  }

  moveFlagpole(hit: HitResult): void {
    const label = this._grabbedTextLabel
    if (!this.world.labelsManager || !label) return

    this.world.targetVisualizer?.show()
    this.world.targetVisualizer?.update(hit.point)

    const findCategoryNodeId = (nodeId: number): number => {
      let targetNode = this.world.currentModel!.apiLayersById.get(nodeId)!

      if (targetNode.parent_id === null) return targetNode.id

      let category = this.world.currentModel!.apiCategoriesById.get(targetNode.parent_id)!

      while (category.parent_id !== null) {
        targetNode = this.world.currentModel!.apiLayersById.get(targetNode.parent_id as number)!
        category = this.world.currentModel!.apiCategoriesById.get(targetNode.parent_id as number)!
      }

      return category!.id
    }

    const categoryId = findCategoryNodeId(hit.nodeId)

    this._grabbedTextLabel = this.world.labelsManager.updateLabel(label.id, {
      point: hit.point,
      nodeId: categoryId,
    })

    this.dispatchEvent({ type: 'label-move', textLabel: label })
  }

  onMouseDownCapture(e: MouseEvent): void {
    if (e.button !== 0) return

    const mousePosition = pointerEventToVec2(e)
    const hit = this.world.hitTest(mousePosition, { includeLabels: true })
    const domHit = domElementsAt(e).find(el => el.dataset.threedEntity)

    this._lastMousePosition = mousePosition

    if (hit?.label && (hit?.grabbable || domHit?.dataset.threedEntity === 'text-label')) {
      // lock controls, prevent event from being dispatched to EditControls
      e.stopImmediatePropagation()

      const isSelectingDifferentLabelFromEditing = this._mode === 'editing' && this._editingTextLabel?.id !== hit.label?.id

      if (this._mode === 'selecting' || isSelectingDifferentLabelFromEditing) {
        this._mode = 'selecting'
        this._editingTextLabel = undefined
        this._grabbedTextLabel = hit.label
        this._isDraggingFlagpole = hit.grabbable

        if (hit.grabbable) {
          this.setCursor(e.currentTarget as HTMLElement, 'grabbing')
          this.world.targetVisualizer?.show()
          this.world.targetVisualizer?.update(hit.point)
        }

        this.dispatchEvent({
          type: 'label-select',
          textLabel: hit.label,
          isRepositioning: hit.grabbable,
        })

        // prevent focus on label dom elements
        e.preventDefault()
      }
    } else if (hit?.drillLabel) {
      // lock controls, prevent event from being dispatched to EditControls
      e.stopImmediatePropagation()

      if (this._mode === 'selecting') {
        this._grabbedDrillLabel = hit.drillLabel
        this.selectDrillLabel(hit.drillLabel)

        // prevent focus on label dom elements
        e.preventDefault()
      }
    } else {
      this._mode = 'selecting'

      if (this._selectedDrillLabel) {
        this.selectDrillLabel(undefined)
      } else {
        this._editingTextLabel = undefined
        this.dispatchEvent({
          type: 'label-select',
          textLabel: undefined,
        })
      }
    }
  }

  private selectDrillLabel(drillLabel: Object3D | undefined) {
    const prevSelectedLabel = this._selectedDrillLabel

    if (drillLabel) {
      this.world.drillDataSetManager?.drillLabelManager.selectDrillLabel(drillLabel?.userData.drillLabelId, {
        prevSelectedLabelId: prevSelectedLabel?.userData.drillLabelId,
      })
      this._selectedDrillLabel = drillLabel
      this.dispatchEvent({ type: 'label-select', drillLabel })
    } else {
      this._selectedDrillLabel = undefined
      this.world.drillDataSetManager?.drillLabelManager.deselectDrillLabel(prevSelectedLabel?.userData.drillLabelId)
    }
  }

  onMouseDown(e: MouseEvent): void {
    if (e.button !== 0) return

    const domHit = domElementsAt(e).find(el => el.dataset.threedEntity)

    if (domHit?.dataset.threedEntity) {
      // lock controls, prevent event from being dispatched to EditControls
      e.stopImmediatePropagation()
    }
  }

  onMouseUp(e: MouseEvent): void {
    if (!(this._grabbedTextLabel || this._grabbedDrillLabel)) return
    if (this._isDraggingFlagpole) {
      this.setCursor(e.currentTarget as HTMLElement, 'grab')
    }

    this._grabbedTextLabel = undefined
    this._grabbedDrillLabel = undefined
    this._isDragging = false
    this.world.targetVisualizer?.hide()
  }

  onMouseLeave(_e: MouseEvent): void {}

  onClick(e: MouseEvent): void {
    if (e.button !== 0) return

    const mousePosition = pointerEventToVec2(e)
    const hit = this.world.hitTest(mousePosition, { includeLabels: true })

    if (!hit?.label) return

    // double click
    if (e.detail >= 2) {
      this._mode = 'editing'
      this._editingTextLabel = hit.label
      this.setCursor(e.currentTarget as HTMLElement, 'text')
    }
  }

  private setCursor(el: HTMLElement, cursor: 'grab' | 'grabbing' | 'move' | 'text' | 'none') {
    if (cursor === 'none') {
      el.style.cursor = 'default'
      return
    }

    el.style.cursor = cursor
  }
}

export { SelectionTool }
